import {
  getCurrentCustomer,
  getFeedbackMessagePage,
  initializeCustomerMessage,
  quitCurrentCustomer,
  sendFeedbackMessage,
  uploadFeedbackImageFile
} from '@/server/customer-service'
import { baseUrl } from '@/utils/request'

export const CUSTOMER_MESSAGE_PAGE_SIZE = 10
export const CUSTOMER_MESSAGE_MAX_LENGTH = 500
export const CUSTOMER_IMAGE_MAX_SIZE = 12 * 1024 * 1024
export const CUSTOMER_EMAIL_MAX_LENGTH = 254

export async function fetchCustomerMessagePage({
  uid = '',
  pageNum = 1,
  lastTime = '',
  firstTime = '',
  headers = {}
} = {}) {
  const response = await getFeedbackMessagePage({
    uid,
    pageNum,
    pageSize: CUSTOMER_MESSAGE_PAGE_SIZE,
    lastTime,
    firstTime,
    headers
  })
  requireSuccess(response, '客服消息加载失败')
  const data = response.data || {}
  const list = Array.isArray(data.list) ? data.list : []

  return {
    messages: list.map(normalizeMessage).filter(item => item.content || item.pictures.length),
    totalPage: Number(data.totalPage || 1) || 1,
    hasMore: list.length >= CUSTOMER_MESSAGE_PAGE_SIZE,
    visitorId: stringValue(response.visitorId || data.visitorId)
  }
}

export async function prepareCustomerSession(uid = '') {
  const initializeResult = await initializeCustomerMessage(uid)
  if (
    initializeResult &&
    initializeResult.success === false &&
    String(initializeResult.statusCode || '') !== '1111'
  ) {
    throw customerError(initializeResult, '客服会话初始化失败')
  }

  const customerResult = await getCurrentCustomer(uid)
  requireSuccess(customerResult, '当前客服信息获取失败')
  return customerResult.data || null
}

export async function submitCustomerFeedback({
  content,
  picture = '',
  uid = '',
  session = null,
  headers = {}
}) {
  const value = stringValue(content)
  if (!value || value.length > CUSTOMER_MESSAGE_MAX_LENGTH) {
    throw new Error(`消息内容应为1-${CUSTOMER_MESSAGE_MAX_LENGTH}个字符`)
  }

  const response = await sendFeedbackMessage({
    category: '其他',
    content: value,
    picture,
    uid,
    consutomId: session && session.id ? session.id : '',
    consutomType: session && session.consutom_type ? session.consutom_type : '',
    callType: session && session.callType ? session.callType : '',
    headers
  })
  requireSuccess(response, '消息发送失败')
  return true
}

export async function uploadCustomerFeedbackImage(file) {
  const formData = new FormData()
  formData.append('pic', file, file.name || 'image.jpg')
  formData.append('user', `${Date.now()}${file.name || 'image.jpg'}`)
  const response = await uploadFeedbackImageFile(formData)
  requireSuccess(response, '图片上传失败')
  const imageUrl = findUploadUrl(response.data)
  if (!imageUrl) throw new Error('图片上传结果无效')
  return imageUrl
}

export async function closeCustomerSession(session) {
  if (!session || !session.id) return false
  const response = await quitCurrentCustomer(session.id)
  requireSuccess(response, '客服会话关闭失败')
  return true
}

function normalizeMessage(item = {}) {
  const role = String(item.type || '').toLowerCase() === 'service' ? 'service' : 'user'
  const rawContent = stringValue(item.content)
  const { pictures, originalPictures } = pictureLists(item, role)
  const content = rawContent === 'pic_info' ? '' : plainContent(rawContent)
  const sourceId = stringValue(item.id || item.feedbackId || item.msgId) || hashString([
    item.time || '',
    role,
    item.picture || '',
    item.picture_small || '',
    rawContent
  ].join('|'))

  return {
    id: `message-${sourceId}`,
    role,
    content,
    picture: pictures[0] || '',
    pictures,
    originalPictures,
    rawTime: stringValue(item.time),
    timestamp: timestampValue(item.time),
    raw: item
  }
}

function imageList(source) {
  return stringValue(source)
    .split(',')
    .map(safeImageUrl)
    .filter(Boolean)
}

function pictureLists(item, role) {
  const originalPictures = imageList(item.picture || item.picture_small)
  const pictures = imageList(role === 'user'
    ? (item.picture_small || item.picture)
    : (item.picture || item.picture_small))
  const length = Math.max(pictures.length, originalPictures.length)

  return {
    pictures: Array.from({ length }, (_, index) => pictures[index] || originalPictures[index]).filter(Boolean),
    originalPictures: Array.from({ length }, (_, index) => originalPictures[index] || pictures[index]).filter(Boolean)
  }
}

function plainContent(value) {
  return stringValue(value)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;|&npsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&')
    .trim()
}

function timestampValue(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1000000000000 ? value * 1000 : value
  }
  const source = stringValue(value)
  if (!source) return 0
  if (/^\d+$/.test(source)) {
    const number = Number(source)
    return number < 1000000000000 ? number * 1000 : number
  }
  const timestamp = new Date(source.replace(/-/g, '/')).getTime()
  return Number.isFinite(timestamp) ? timestamp : 0
}

function findUploadUrl(value, visited = new Set()) {
  const directValue = safeImageUrl(value)
  if (directValue) return directValue
  if (!value || typeof value !== 'object' || visited.has(value)) return ''
  visited.add(value)
  const keys = ['url', 'picture', 'image', 'img', 'imageUrl', 'pictureUrl', 'fileUrl', 'src', 'path', 'fullPath', 'link']
  for (const key of keys) {
    const result = findUploadUrl(value[key], visited)
    if (result) return result
  }
  for (const item of Object.values(value)) {
    const result = findUploadUrl(item, visited)
    if (result) return result
  }
  return ''
}

function safeImageUrl(value) {
  const candidate = stringValue(value)
  if (!candidate) return ''
  try {
    const url = new URL(candidate, baseUrl)
    return ['http:', 'https:', 'blob:'].includes(url.protocol) ? url.toString() : ''
  } catch (error) {
    return ''
  }
}

function stringValue(value) {
  return value === null || value === undefined ? '' : String(value).trim()
}

function hashString(value) {
  let hash = 0
  const source = String(value || '')
  for (let index = 0; index < source.length; index += 1) {
    hash = ((hash << 5) - hash) + source.charCodeAt(index)
    hash |= 0
  }
  return Math.abs(hash).toString(36)
}

function requireSuccess(response, fallback) {
  if (!response) throw new Error(fallback)
  if (response.success === false) throw customerError(response, fallback)
  return response
}

function customerError(response, fallback) {
  const error = new Error((response && response.message) || fallback)
  error.statusCode = response && response.statusCode
  return error
}
